import {
  useIsRestoring,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import { parseAppointmentApiDateTime } from "@/lib/utils/date";
import type { GetAppointmentBookingResponse } from "@/openapi/generated";
import {
  appointmentBookingsBulkQueryKey,
  uniqueSortedAppointmentIds,
} from "../utils/appointment-bookings-query";
import { participantOptionLabel } from "../utils/appointment-variants";
import { fetchAndSeedAppointmentBookingsBulk } from "../utils/fetch-appointment-bookings-bulk";

interface UseUserAppointmentBookingsProps {
  appointmentIds: number[];
  studentIds: number[];
  refetchOnMount?: boolean | "always";
}

export interface UserBooking {
  bookingId: number;
  appointmentId: number;
  appointmentName: string;
  startTime: Date;
  endTime: Date;
  status: string;
  studentName: string;
  studentId: number;
  classRoomName: string | null;
  reason: string | null;
  createdAt: Date;
  optionLabel: string | null;
  locationName: string | null;
  instructorNotes: string | null;
}

export const useUserAppointmentBookings = ({
  appointmentIds,
  studentIds,
  refetchOnMount = false,
}: UseUserAppointmentBookingsProps) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const isRestoring = useIsRestoring();

  const stableAppointmentIds = useMemo(
    () => uniqueSortedAppointmentIds(appointmentIds),
    [appointmentIds],
  );

  const bookingsCanFetch =
    !!accessToken &&
    studentIds.length > 0 &&
    stableAppointmentIds.length > 0 &&
    !isRestoring;

  const {
    data: bulkItems,
    isPending,
    isFetching,
  } = useQuery({
    queryKey: appointmentBookingsBulkQueryKey(stableAppointmentIds),
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      return fetchAndSeedAppointmentBookingsBulk(
        queryClient,
        accessToken,
        stableAppointmentIds,
      );
    },
    enabled: bookingsCanFetch,
    refetchOnMount,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const bookingsByAppointmentId = useMemo(() => {
    const map = new Map<number, GetAppointmentBookingResponse[]>();
    for (const item of bulkItems ?? []) {
      map.set(Number(item.appointmentId), item.bookings ?? []);
    }
    return map;
  }, [bulkItems]);

  const bookingsMap = useMemo(() => {
    const map = new Map<number, boolean>();

    for (const appointmentId of appointmentIds) {
      const bookings = bookingsByAppointmentId.get(appointmentId);
      if (bookings && Array.isArray(bookings)) {
        const hasBooking = bookings.some((booking) =>
          studentIds.includes(Number(booking.student.studentId)),
        );
        map.set(appointmentId, hasBooking);
      } else {
        map.set(appointmentId, false);
      }
    }

    return map;
  }, [bookingsByAppointmentId, appointmentIds, studentIds]);

  const userBookings = useMemo(() => {
    const bookings: UserBooking[] = [];

    for (const appointmentId of appointmentIds) {
      const appointmentBookings = bookingsByAppointmentId.get(appointmentId);
      if (!appointmentBookings || !Array.isArray(appointmentBookings)) continue;

      const userStudentBookings = appointmentBookings.filter((booking) =>
        studentIds.includes(Number(booking.student.studentId)),
      );

      userStudentBookings.forEach((booking: GetAppointmentBookingResponse) => {
        bookings.push({
          bookingId: Number(booking.bookingId),
          appointmentId,
          appointmentName: "",
          startTime:
            parseAppointmentApiDateTime(booking.startTime) ??
            new Date(booking.startTime),
          endTime:
            parseAppointmentApiDateTime(booking.endTime) ??
            new Date(booking.endTime),
          status: booking.status || "Pending",
          studentName: `${booking.student.firstName} ${booking.student.lastName}`,
          studentId: Number(booking.student.studentId),
          classRoomName: booking.classRoom?.name || null,
          optionLabel: participantOptionLabel(
            booking.participantCount == null
              ? null
              : Number(booking.participantCount),
          ),
          locationName:
            booking.locationName ?? booking.classRoom?.location?.name ?? null,
          instructorNotes: booking.instructorNotes?.trim() || null,
          reason: booking.reason || null,
          createdAt:
            parseAppointmentApiDateTime(booking.createdAt) ??
            new Date(booking.createdAt),
        });
      });
    }

    const seenBookingIds = new Set<number>();
    return bookings.filter((booking) => {
      if (!Number.isFinite(booking.bookingId)) return true;
      if (seenBookingIds.has(booking.bookingId)) return false;
      seenBookingIds.add(booking.bookingId);
      return true;
    });
  }, [bookingsByAppointmentId, appointmentIds, studentIds]);

  const isLoading = bookingsCanFetch && (isPending || isFetching);

  return {
    bookingsMap,
    userBookings,
    isLoading,
  };
};
